// Komentáře: sdílené prvky vlákna komentářů (bublina, barva avataru, formát času).
// 14. 9. 2026 (fáze 6): přesunuto beze změny z ClientDetail.jsx, aby stejné bubliny
// mohl použít i detail potenciálního trenéra (/hr/kandidati).

import React, { useState } from "react";
import { Pin, PinOff, Archive, ArchiveRestore, Trash2, Pencil } from "lucide-react";
import { useUzkaObrazovka } from "../lib/obrazovka.js";

// Stabilní barva avataru per autor (hash z emailu/jména) — vizuálně rozliší autory.
export function getAvatarColor(autor) {
  const palette = [
    { bg: "bg-blue-100", text: "text-blue-800" },
    { bg: "bg-emerald-100", text: "text-emerald-800" },
    { bg: "bg-brand-100", text: "text-brand-800" },
    { bg: "bg-rose-100", text: "text-rose-800" },
    { bg: "bg-amber-100", text: "text-amber-800" },
    { bg: "bg-cyan-100", text: "text-cyan-800" },
    { bg: "bg-pink-100", text: "text-pink-800" },
    { bg: "bg-plum-100", text: "text-plum-800" },
  ];
  const s = String(autor || "");
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = ((hash << 5) - hash + s.charCodeAt(i)) | 0;
  return palette[Math.abs(hash) % palette.length];
}

// Relativní formát času: "před 5 min", "před 2 hod", "včera 14:30", "5. 5. 2026 14:30"
export function formatCommentTime(iso) {
  if (!iso) return "";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (!m) return iso;
  const [, y, mo, d, h, mi] = m;
  const dt = new Date(`${y}-${mo}-${d}T${h}:${mi}:00`);
  const now = new Date();
  const diffMin = Math.round((now - dt) / 60000);
  if (diffMin < 1) return "právě teď";
  if (diffMin < 60) return `před ${diffMin} min`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `před ${diffHr} hod`;
  const diffDays = Math.round(diffHr / 24);
  if (diffDays === 1) return `včera ${h}:${mi}`;
  if (diffDays < 7) return `před ${diffDays} dny`;
  const sameYear = now.getFullYear() === parseInt(y, 10);
  return sameYear ? `${parseInt(d, 10)}. ${parseInt(mo, 10)}. ${h}:${mi}` : `${parseInt(d, 10)}. ${parseInt(mo, 10)}. ${y}`;
}

// CommentBubble — jeden komentář v threadu (avatar iniciála + jméno + datum + text + akce).
// Hover ukazuje akce (pin / archive / delete). Pinned bubble má žlutý akcent + ikona připíchnutí,
// archived je ztlumený. Avatar má stabilní barvu per autor.
// Delete tlačítko je vidět JEN pro role=owner (canDelete prop), s irreversibilním warningem v dialogu.
export function CommentBubble({ comment, canEdit, canDelete, canEditThis, onPin, onArchive, onDelete, onEdit }) {
  const autor = comment.autor_name || comment.autor_email || "?";
  const initials = autor.split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "?";
  const isPinned = !!comment.pinned;
  const isArchived = !!comment.archived;
  const avatarColor = getAvatarColor(comment.autor_email || autor);
  const timeLabel = formatCommentTime(comment.vytvoreno);

  // Edit mode lokalni state — neprenasi se na rodice dokud user neulozi.
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(comment.text);
  const [editSaving, setEditSaving] = useState(false);
  // 5.9.2026 (fáze 4b): na telefonu jsou akce komentáře vidět pořád, protože
  // najet myší tam nejde a jinak se s komentářem nedá nic dělat.
  const uzka = useUzkaObrazovka();

  const startEdit = () => {
    setEditText(comment.text);
    setIsEditing(true);
  };
  const cancelEdit = () => {
    setIsEditing(false);
    setEditText(comment.text);
  };
  const saveEdit = async () => {
    const trimmed = editText.trim();
    if (!trimmed || trimmed === comment.text || editSaving) {
      if (trimmed === comment.text) setIsEditing(false);
      return;
    }
    setEditSaving(true);
    try {
      await onEdit(comment, trimmed);
      setIsEditing(false);
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <div
      className={`group relative rounded-xl transition-all ${
        isPinned
          ? "bg-brand-gold/10 border border-brand-gold/40"
          : isArchived
            ? "bg-slate-50/80 border border-slate-200 opacity-60"
            : "bg-white border border-slate-200 hover:border-slate-300 hover:shadow-sm"
      }`}
    >

      <div className="flex items-start gap-3 p-3">
        {/* Avatar — barevná iniciála per autor */}
        <div
          className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${avatarColor.bg} ${avatarColor.text} ring-2 ring-white shadow-sm`}
          title={autor}
        >
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap mb-1">
            <span className="text-sm font-semibold text-slate-800">{autor}</span>
            <span className="text-xs text-slate-400" title={comment.vytvoreno}>{timeLabel}</span>
            {isPinned && (
              <span className="inline-flex items-center gap-1 rounded-full bg-brand-gold/30 px-2 py-0.5 text-[11px] font-bold text-brand-950">
                <Pin className="h-3 w-3" /> Připnuto
              </span>
            )}
            {isArchived && (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-bold text-slate-600">
                <Archive className="h-3 w-3" /> Archiv
              </span>
            )}
          </div>
          {!isEditing ? (
            <>
              <div className="text-sm text-slate-700 whitespace-pre-line break-words leading-relaxed">{comment.text}</div>
              {comment.aktualizovano && comment.aktualizovano !== comment.vytvoreno && (
                <div className="mt-1.5 text-[11px] italic text-slate-400">upraveno {formatCommentTime(comment.aktualizovano)}</div>
              )}
            </>
          ) : (
            <div className="space-y-2 mt-1">
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); saveEdit(); }
                }}
                rows={Math.max(2, Math.min(8, (editText.match(/\n/g) || []).length + 2))}
                disabled={editSaving}
                autoFocus
                data-gramm="false"
                className="field w-full resize-y disabled:opacity-50"
              />
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={saveEdit}
                  disabled={editSaving || !editText.trim() || editText.trim() === comment.text}
                  className="btn btn-primary"
                  title="Uložit (⌘+Enter)"
                >
                  {editSaving ? "Ukládám…" : "Uložit"}
                </button>
                <button
                  type="button"
                  onClick={cancelEdit}
                  disabled={editSaving}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors disabled:opacity-50 min-h-[44px] sm:min-h-0"
                  title="Zrušit (Esc)"
                >
                  Zrušit
                </button>
                <span className="text-[11px] text-slate-400 ml-1">⌘+Enter uloží, Esc zruší</span>
              </div>
            </div>
          )}
        </div>
        {/* Akce — viditelné na hover. Edit jen pro vlastni komentare (nebo owner override). */}
        {canEdit && !isEditing && !uzka && (
          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity flex-shrink-0">
            {canEditThis && (
              <button
                type="button"
                onClick={startEdit}
                className="p-1.5 rounded-lg hover:bg-plum-100 text-slate-400 hover:text-plum-600 transition-colors min-h-[44px] sm:min-h-0"
                title="Upravit text"
              >
                <Pencil className="w-4 h-4" />
              </button>
            )}
            <button
              type="button"
              onClick={() => onPin(comment, !isPinned)}
              className={`p-1.5 rounded-lg hover:bg-amber-100 ${isPinned ? "text-amber-600" : "text-slate-400 hover:text-amber-600"} transition-colors`}
              title={isPinned ? "Odepnout" : "Připnout"}
            >
              {isPinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
            </button>
            <button
              type="button"
              onClick={() => onArchive(comment, !isArchived)}
              className={`p-1.5 rounded-lg hover:bg-blue-100 ${isArchived ? "text-blue-600" : "text-slate-400 hover:text-blue-600"} transition-colors`}
              title={isArchived ? "Obnovit z archivu" : "Skrýt do archivu"}
            >
              {isArchived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
            </button>
            {/* Delete tlačítko — jen owner. Hard delete + confirm warning. */}
            {canDelete && (
              <button
                type="button"
                onClick={() => onDelete(comment)}
                className="p-1.5 rounded-lg hover:bg-rose-100 text-slate-400 hover:text-rose-600 transition-colors"
                title="Smazat trvale (jen owner)"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>
      {/* Telefon: akce mají vlastní řádek pod textem, jsou vidět pořád. */}
      {canEdit && !isEditing && uzka && (
        <div className={`flex items-center gap-1 border-t px-3 py-1.5 ${isPinned ? "border-brand-gold/30" : "border-slate-100"}`}>
          {canEditThis && (
            <button type="button" onClick={startEdit} aria-label="Upravit text" title="Upravit text"
              className="flex h-9 w-9 items-center justify-center rounded-xl text-slate-500 active:bg-slate-100">
              <Pencil className="h-4 w-4" />
            </button>
          )}
          <button type="button" onClick={() => onPin(comment, !isPinned)} aria-label={isPinned ? "Odepnout" : "Připnout"} title={isPinned ? "Odepnout" : "Připnout"}
            className={`flex h-9 w-9 items-center justify-center rounded-xl ${isPinned ? "bg-brand-gold/25 text-brand-950" : "text-slate-500 active:bg-slate-100"}`}>
            {isPinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
          </button>
          <button type="button" onClick={() => onArchive(comment, !isArchived)} aria-label={isArchived ? "Obnovit z archivu" : "Skrýt do archivu"} title={isArchived ? "Obnovit z archivu" : "Skrýt do archivu"}
            className="flex h-9 w-9 items-center justify-center rounded-xl text-slate-500 active:bg-slate-100">
            {isArchived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
          </button>
          {canDelete && (
            <button type="button" onClick={() => onDelete(comment)} aria-label="Smazat trvale" title="Smazat trvale (jen owner)"
              className="ml-auto flex h-9 w-9 items-center justify-center rounded-xl text-rose-500 active:bg-rose-50">
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
